import React, { useState } from 'react';
import {
  List,
  Datagrid,
  DatagridBody,
  DatagridRow,
  TextField,
  Responsive,
  SimpleList,
  TopToolbar,
  ReferenceManyField,
  SelectField,
  Query,
  TextInput,
  AutocompleteInput,
  Pagination
} from 'react-admin';

import {
  Paper,
  ListItem,
  ListItemText,
  Collapse,
  List as ListView,
  Table,
  TableBody,
  CircularProgress
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import MailIcon from '@material-ui/icons/Mail';
import { size, omit } from 'lodash';

import ListTitleBar from '../../core/components/ListTitleBar';
import BulkActionDeleteButton from '../../core/components/BulkActionDeleteButton';
import GridPopover from '../../core/components/grid/GridPopover';
import { ChevronRightIcon } from '../../core/themes/icons/ChevronRightIcon';
import { ChevronDownIcon } from '../../core/themes/icons/ChevronDownIcon';
import Filter from '../../core/components/list/StyledFilter';
import LinkField from '../../core/components/LinkField';
import Empty from '../../core/components/list/Empty';
import {
  mailTemplateTypes,
  passthroughTemplateType
} from './MailTemplateLookups';
import {
  predictiveSearchFormatter,
  predictiveSearchParser
} from '../../core/utils';

const useStyles = makeStyles({
  dataGridHead: {
    '& tr': {
      '& th:nth-of-type(1)': {
        width: '24px'
      }
    }
  },
  root: {
    marginRight: 'auto'
  },
  rootListItem: {
    paddingBottom: '0px',
    paddingTop: '0px'
  },
  button: {
    padding: '0px'
  },
  primary: {
    color: '#212121',
    fontSize: '14px',
    paddingLeft: '8px'
  },
  emptyText: {
    color: '#757575',
    fontSize: '14px'
  },
  nested: {
    paddingLeft: '43px',
    pointerEvents: 'none',
    paddingBottom: '0px',
    paddingTop: '0px'
  },
  textInput: {
    minWidth: '240px'
  }
});

const Actions = ({
  displayedFilters,
  filters,
  filterValues,
  resource,
  showFilter
}) => {
  const classes = useStyles();

  return (
    <TopToolbar classes={{ root: classes.root }}>
      {filters &&
        React.cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button'
        })}
    </TopToolbar>
  );
};

const MailTemplateFilters = props => {
  const classes = useStyles();

  return (
    <Filter variant="standard" {...props}>
      <TextInput
        label="Search by Email Template Description"
        source="description"
        alwaysOn
        className={classes.textInput}
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
      />
      <AutocompleteInput
        label="Filter by Email Type"
        source="type"
        choices={mailTemplateTypes.concat(passthroughTemplateType)}
        allowEmpty
        alwaysOn
        options={{
          suggestionsContainerProps: {
            style: {
              backgroundColor: 'white',
              border: '1px solid #E0E0E0',
              boxShadow:
                ' 0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)'
            }
          }
        }}
      />
    </Filter>
  );
};

const EmailNotification = ({ data = [] }) => {
  const classes = useStyles();

  if (size(data) > 0) {
    return (
      <GridPopover
        text={`${
          size(data) > 1
            ? 'Multiple Categories'
            : Object.values(data).map(item => item.name)
        } `}
        classes={{ button: classes.button }}
        id="automated-email-notification"
        popoverContent={
          <Paper>
            <Table>
              <TableBody>
                <ListView>
                  {Object.values(data).map((item, index) => {
                    return <Collapsible item={item} key={index} />;
                  })}
                </ListView>
              </TableBody>
            </Table>
          </Paper>
        }
      />
    );
  } else {
    return null;
  }
};

const Collapsible = ({ item }) => {
  const classes = useStyles();
  const [expand, setExpand] = useState(false);

  const handleClick = () => {
    setExpand(!expand);
  };

  return (
    <>
      <ListItem
        button
        onClick={handleClick}
        classes={{ root: classes.rootListItem }}
      >
        {expand ? <ChevronDownIcon /> : <ChevronRightIcon />}
        <ListItemText
          primary={`${item.name} `}
          classes={{ primary: classes.primary }}
        ></ListItemText>
      </ListItem>
      <Collapse in={expand} timeout="auto" unmountOnExit>
        <ListView component="div" disablePadding>
          <Query
            type="GET_MANY_REFERENCE"
            resource="subcategories"
            payload={{
              target: 'categoryId',
              id: item.id
            }}
          >
            {({ data, total, loading, error }) => {
              if (!loading && total > 0) {
                return data.map((item, index) => {
                  return (
                    <ListItem button className={classes.nested} key={index}>
                      <ListItemText
                        primary={`${item.name}`}
                        classes={{ primary: classes.primary }}
                      />
                    </ListItem>
                  );
                });
              } else if (loading) {
                return (
                  <ListItem button className={classes.nested}>
                    <CircularProgress size={20} thickness={2} />
                  </ListItem>
                );
              } else {
                return (
                  <ListItem button className={classes.nested}>
                    <ListItemText
                      primary="There are no related subcategories to be displayed"
                      classes={{ primary: classes.emptyText }}
                    />
                  </ListItem>
                );
              }
            }}
          </Query>
        </ListView>
      </Collapse>
    </>
  );
};

const MailTemplateTitle = props => {
  return (
    <ListTitleBar
      type="Email Templates"
      title="New Email Template"
      createUrl="#/mailtemplates/create"
      icon={<MailIcon />}
      hasCreatePermission={props.hasCreatePermission}
      buttonId="new-email-template"
    />
  );
};

const MailTemplateList = props => {
  const classes = useStyles();
  const {
    hasCreatePermission,
    hasEditPermission,
    hasDeletePermission,
    ...rest
  } = props;

  return (
    <List
      {...omit(rest, ['userProfile'])}
      exporter={false}
      title={<MailTemplateTitle hasCreatePermission={hasCreatePermission} />}
      perPage={20}
      pagination={<Pagination rowsPerPageOptions={[20, 25, 30]} />}
      bulkActionButtons={
        hasDeletePermission && (
          <BulkActionDeleteButton resource="mailtemplates" />
        )
      }
      actions={<Actions />}
      sort={{ field: 'description', order: 'ASC' }}
      filters={<MailTemplateFilters />}
      empty={<Empty />}
      syncWithLocation
    >
      <Responsive
        small={
          <SimpleList
            primaryText={record => record.subject}
            secondaryText={record => record.description}
            tertiaryText={() => ''}
          />
        }
        medium={
          <Datagrid
            classes={{ thead: hasDeletePermission && classes.dataGridHead }}
            body={<DatagridBody row={<DatagridRow />} />}
          >
            <LinkField label="Description" source="description" />
            <SelectField
              label="Email Type"
              source="type"
              choices={mailTemplateTypes.concat(passthroughTemplateType)}
              id="email type"
            />
            <ReferenceManyField
              label="Automated Email Notification"
              reference="categories"
              sortable={false}
              target="templateId"
            >
              <EmailNotification />
            </ReferenceManyField>
            <TextField source="id" label="ID" id="id" />
          </Datagrid>
        }
      />
    </List>
  );
};

export default MailTemplateList;
